import React, { useState, useEffect } from 'react';
// **** Main CSS **** //
import './assets/scss/main.scss';

import { useSelector, useDispatch } from "react-redux";
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { adminAuthActions } from './store/adminAuth';
import { resetFilters } from './store/orderFilters';
import { resetUserFilters } from './store/userFilters';

import Login from './backoffice/login/Login';
import Users from './backoffice/users/Users';
import Orders from './backoffice/orders/Orders'
import OrderDetails from './backoffice/order-details/OrderDetails';
import UserEsims from './backoffice/users/UserEsims'
import Settings from './backoffice/settings/Settings';
import KycCountries from './backoffice/kyc-countries/KycCountries';
import DeviceTypes from './backoffice/device-types/DeviceTypes';
import NotFound from './backoffice/not-found/NotFound';
import PriceAdjustment from './backoffice/price-adjustment/PriceAdjustment';
import WholesalePriceAdjustment from './backoffice/wholesale-price-adjustment/WholesalePriceAdjustment';

import SubscriptionPlans from './backoffice/subscription-plans/SubscriptionPlans';
import Dashboard from './backoffice/dashboard/Dashboard';
import PromoCode from './backoffice/promo-code/PromoCode';
import Affiliates from './backoffice/affiliates/Affiliates';
import Cms from './backoffice/cms/Cms';
import EmailTemplates from './backoffice/email-templates/EmailTemplates';
import PrepaidRetailVouchers from './backoffice/prepaid-retail-vouchers/PrepaidRetailVouchers';
import EsimIccid from './backoffice/esimDetails/EsimIccid';
import EsimDetails from './backoffice/esimDetails/EsimDetails';
import Dashboard2 from './backoffice/dashboard/Dashboard2';
import CustomerDetails from './backoffice/customer-details/CustomerDetails';
import PushNotifications from './backoffice/marketing/push-notifications';
import AdminUsers from './backoffice/admin-users/AdminUsers';
import NoAccess from './backoffice/no-access/NoAccess';
import ProtectedRoute from './components/ProtectedRoute';
import { PERMISSIONS, hasPermission } from './utils/permissions';
import { appName } from './GlobalConfig';

function App() {
  const isAuthAdmin = useSelector(state => state.adminAuth.adminLoggedIn);
  const adminData = useSelector(state => state.adminAuth.adminSessionData);
  const dispatch = useDispatch();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isAuthAdmin && (adminData?.permission === undefined || adminData?.permission === null)) {
      dispatch(adminAuthActions.onLogout());
      dispatch(resetFilters());
      dispatch(resetUserFilters());
    }
  }, [isAuthAdmin, adminData, dispatch]);

  const getDefaultRoute = () => {
    if (hasPermission(adminData?.permission, PERMISSIONS.VIEW_CONTENT) && !hasPermission(adminData?.permission, PERMISSIONS.VIEW_ORDERS) && !hasPermission(adminData?.permission, PERMISSIONS.VIEW_USERS)) {
      return '/cms';
    }
    if (hasPermission(adminData?.permission, PERMISSIONS.VIEW_AFFILIATES) && !hasPermission(adminData?.permission, PERMISSIONS.VIEW_ORDERS) && !hasPermission(adminData?.permission, PERMISSIONS.VIEW_USERS)) {
      return '/affiliates';
    }
    if (hasPermission(adminData?.permission, [PERMISSIONS.VIEW_PROMOS, PERMISSIONS.MANAGE_PROMOS]) && !hasPermission(adminData?.permission, PERMISSIONS.VIEW_ORDERS) && !hasPermission(adminData?.permission, PERMISSIONS.VIEW_USERS)) {
      return '/vouchers';
    }
    if (hasPermission(adminData?.permission, [PERMISSIONS.VIEW_PREPAID_VOUCHERS, PERMISSIONS.MANAGE_PREPAID_VOUCHERS]) && !hasPermission(adminData?.permission, PERMISSIONS.VIEW_ORDERS) && !hasPermission(adminData?.permission, PERMISSIONS.VIEW_USERS)) {
      return '/prepaid-retail-vouchers';
    }
    return '/dashboard';
  };

  return (
    <Router>
      <Routes>
        <Route path='/' element={<Navigate to='/login' />} />
        <Route path='/login' element={!isAuthAdmin ? <Login /> : <Navigate to={getDefaultRoute()} />} />
        <Route path='/dashboard' element={
          isAuthAdmin ? (
            hasPermission(adminData?.permission, PERMISSIONS.VIEW_ORDERS) || hasPermission(adminData?.permission, PERMISSIONS.VIEW_USERS) ? (
              ((appName === 'data2go' || appName === 'esimcrew') && !isMobile) ? <Dashboard2 /> : <Dashboard />
            ) : (
              <Navigate to={getDefaultRoute()} />
            )
          ) : (
            <Navigate to='/login' />
          )
        } />
        <Route path='/old-dashboard' element={isAuthAdmin ? (hasPermission(adminData?.permission, PERMISSIONS.VIEW_ORDERS) || hasPermission(adminData?.permission, PERMISSIONS.VIEW_USERS) ? <Dashboard /> : <Navigate to={getDefaultRoute()} />) : <Navigate to='/login' />} />
        <Route path='/dashboard2' element={isAuthAdmin ? <Dashboard2 /> : <Navigate to='/login' />} />
        <Route path='/users' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_USERS}>
            <Users />
          </ProtectedRoute>
        } />
        <Route path='/customer-details/:id' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_USERS}>
            <CustomerDetails />
          </ProtectedRoute>
        } />
        <Route path='/affiliates' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_AFFILIATES}>
            <Affiliates />
          </ProtectedRoute>
        } />
        <Route path='/orders' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_ORDERS}>
            <Orders />
          </ProtectedRoute>
        } />
        <Route path='/orders/:userid' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_ORDERS}>
            <Orders />
          </ProtectedRoute>
        } />
        <Route path='/user-orders/:userid' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_ORDERS}>
            <Orders />
          </ProtectedRoute>
        } />
        <Route path='/user-esims/:userid' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_USERS}>
            <UserEsims />
          </ProtectedRoute>
        } />
        <Route path='/:screen/details/:orderid' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_ORDERS}>
            <OrderDetails />
          </ProtectedRoute>
        } />
        <Route path='/settings' element={isAuthAdmin ? <Settings /> : <Navigate to='/login' />} />
        <Route path='/kyc-countries' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_CONFIG}>
            <KycCountries />
          </ProtectedRoute>
        } />
        <Route path='/device-types' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_CONFIG}>
            <DeviceTypes />
          </ProtectedRoute>
        } />
        <Route path='/cms' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_CONTENT}>
            <Cms />
          </ProtectedRoute>
        } />
        <Route path='/esim-details' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_ORDERS}>
            <EsimIccid />
          </ProtectedRoute>
        } />
        <Route path='/esim-details/:searchTerm' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_ORDERS}>
            <EsimDetails />
          </ProtectedRoute>
        } />
        <Route path='/price-adjustment' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_PRICES}>
            <PriceAdjustment />
          </ProtectedRoute>
        } />
        <Route path='/wholesale-price-adjustment' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_PRICES}>
            <WholesalePriceAdjustment />
          </ProtectedRoute>
        } />

        <Route path='/subscription-plans' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_PRICES}>
            <SubscriptionPlans />
          </ProtectedRoute>
        } />
        <Route path='/vouchers' element={
          <ProtectedRoute requiredPermission={[PERMISSIONS.VIEW_PROMOS, PERMISSIONS.MANAGE_PROMOS]}>
            <PromoCode />
          </ProtectedRoute>
        } />
        <Route path='/prepaid-retail-vouchers' element={
          <ProtectedRoute requiredPermission={[PERMISSIONS.VIEW_PREPAID_VOUCHERS, PERMISSIONS.MANAGE_PREPAID_VOUCHERS]}>
            <PrepaidRetailVouchers />
          </ProtectedRoute>
        } />
        <Route path='/push-notifications' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_PUSH_NOTIFICATIONS}>
            <PushNotifications />
          </ProtectedRoute>
        } />
        <Route path='/admin-users' element={
          <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_ADMINS}>
            <AdminUsers />
          </ProtectedRoute>
        } />
        <Route path='/email-templates' element={
          process.env.REACT_APP_ENVIRONMENT === 'dev' ? (
            <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_EMAIL_TEMPLATES}>
              <EmailTemplates />
            </ProtectedRoute>
          ) : (
            isAuthAdmin ? <Navigate to='/not-found' /> : <Navigate to='/login' />
          )
        } />
        <Route path='/no-access' element={isAuthAdmin ? <NoAccess /> : <Navigate to='/' />} />
        <Route path='/not-found' element={isAuthAdmin ? <NotFound /> : <Navigate to='/' />} />
        <Route path='*' element={isAuthAdmin ? <Navigate to='/not-found' /> : <Navigate to='/' />} />
      </Routes>
    </Router>
  );
}
export default App;